import { useLayoutEffect, useRef } from 'react'

// Long enough for antd's dialog zoom to finish and move focus into the
// dialog. That focus, and the body scroll lock, both drop window scroll to
// 0 because this app scrolls `html` (see styles/core.scss).
const HOLD_FRAMES = 24

function restoreWindowScroll(left: number, top: number) {
  if (typeof document === 'undefined') return
  const scrollingElement = document.scrollingElement
  const elementLeft = scrollingElement?.scrollLeft ?? left
  const elementTop = scrollingElement?.scrollTop ?? top
  const alreadyThere =
    window.scrollX === left &&
    window.scrollY === top &&
    elementLeft === left &&
    elementTop === top
  if (alreadyThere) return
  window.scrollTo(left, top)
  if (scrollingElement != null) {
    scrollingElement.scrollLeft = left
    scrollingElement.scrollTop = top
  }
}

/**
 * Run `action` without letting the page jump to the top. Holds the scroll
 * position across the frames where a dialog lock or focus would reset it.
 */
export function holdWindowScroll(action: () => void) {
  const top = window.scrollY
  const left = window.scrollX
  action()
  let frame = 0
  const step = () => {
    if (typeof document === 'undefined') return
    restoreWindowScroll(left, top)
    frame += 1
    if (frame < HOLD_FRAMES) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

/**
 * Snapshot scroll while rendering the open transition — before the dialog
 * lock's layout effect — and put it back before paint, again when the
 * dialog finishes opening and takes focus, and again when it closes.
 */
export function useRestoreWindowScroll(open: boolean) {
  const positionRef = useRef({ top: 0, left: 0 })
  const wasOpenRef = useRef(false)

  if (open && !wasOpenRef.current && typeof window !== 'undefined') {
    positionRef.current = { top: window.scrollY, left: window.scrollX }
  }
  wasOpenRef.current = open

  useLayoutEffect(() => {
    if (!open) return undefined
    const { top, left } = positionRef.current
    restoreWindowScroll(left, top)
    const frame = requestAnimationFrame(() => restoreWindowScroll(left, top))
    return () => {
      cancelAnimationFrame(frame)
      requestAnimationFrame(() => restoreWindowScroll(left, top))
    }
  }, [open])

  return (nextOpen: boolean) => {
    if (!nextOpen) return
    const { top, left } = positionRef.current
    restoreWindowScroll(left, top)
  }
}
